/**
 * useSyncSuggestionsMap - Hook to map sync suggestions to entity pairs
 *
 * Creates a lookup map so we can quickly find sync suggestions
 * for a given Finch entity, Filevine entity, or entity link.
 *
 * Filters out NO_CHANGE suggestions as they don't represent actionable changes.
 */

import { useMemo } from 'react';

import type { FilevineEntitySyncSuggestionSetSchema } from '@/services/api/generated/entitySyncService';

import { hasActionableSuggestions } from './useActionableSuggestions';

export interface SyncSuggestionsByEntity {
  /** Map from Finch entity ID (finch_entity_id) to suggestion sets */
  byFinchId: Map<string, FilevineEntitySyncSuggestionSetSchema[]>;
  /** Map from Filevine entity ID (filevine_collection_item_id) to suggestion sets */
  byFilevineId: Map<string, FilevineEntitySyncSuggestionSetSchema[]>;
  /** Map from entity link ID to suggestion sets */
  byLinkId: Map<string, FilevineEntitySyncSuggestionSetSchema[]>;
  /** All pending suggestion sets with actionable changes */
  pendingSets: FilevineEntitySyncSuggestionSetSchema[];
}

/**
 * Hook that creates lookup maps for sync suggestions
 */
export function useSyncSuggestionsMap(
  suggestionSets: FilevineEntitySyncSuggestionSetSchema[] | undefined,
): SyncSuggestionsByEntity {
  return useMemo(() => {
    const byFinchId = new Map<
      string,
      FilevineEntitySyncSuggestionSetSchema[]
    >();
    const byFilevineId = new Map<
      string,
      FilevineEntitySyncSuggestionSetSchema[]
    >();
    const byLinkId = new Map<string, FilevineEntitySyncSuggestionSetSchema[]>();
    const pendingSets: FilevineEntitySyncSuggestionSetSchema[] = [];

    if (!suggestionSets) {
      return { byFinchId, byFilevineId, byLinkId, pendingSets };
    }

    suggestionSets.forEach((set) => {
      // Only include sets that have actionable suggestions (PENDING or PARTIALLY_RESOLVED)
      if (!hasActionableSuggestions(set)) return;

      pendingSets.push(set);

      // Index by Finch entity ID
      if (set.finch_entity_id) {
        const existing = byFinchId.get(set.finch_entity_id) ?? [];
        existing.push(set);
        byFinchId.set(set.finch_entity_id, existing);
      }

      // Index by Filevine entity ID (filevine_collection_item_id)
      if (set.filevine_collection_item_id) {
        const existing =
          byFilevineId.get(set.filevine_collection_item_id) ?? [];
        existing.push(set);
        byFilevineId.set(set.filevine_collection_item_id, existing);
      }

      // Index by link ID
      if (set.entity_link_id) {
        const existing = byLinkId.get(set.entity_link_id) ?? [];
        existing.push(set);
        byLinkId.set(set.entity_link_id, existing);
      }
    });

    return { byFinchId, byFilevineId, byLinkId, pendingSets };
  }, [suggestionSets]);
}

/**
 * Get sync suggestions for a specific entity pair
 */
export function getSuggestionsForEntityPair(
  map: SyncSuggestionsByEntity,
  finchEntityId: string | null | undefined,
  filevineEntityId: string | null | undefined,
  linkId: string | null | undefined,
): FilevineEntitySyncSuggestionSetSchema[] {
  const suggestions: FilevineEntitySyncSuggestionSetSchema[] = [];
  const seenIds = new Set<string>();

  // Check by link ID first (most specific)
  if (linkId) {
    const byLink = map.byLinkId.get(linkId);
    byLink?.forEach((s) => {
      if (!seenIds.has(s.id)) {
        seenIds.add(s.id);
        suggestions.push(s);
      }
    });
  }

  // Check by Finch entity ID
  if (finchEntityId) {
    const byFinch = map.byFinchId.get(finchEntityId);
    byFinch?.forEach((s) => {
      if (!seenIds.has(s.id)) {
        seenIds.add(s.id);
        suggestions.push(s);
      }
    });
  }

  // Check by Filevine entity ID
  if (filevineEntityId) {
    const byFilevine = map.byFilevineId.get(filevineEntityId);
    byFilevine?.forEach((s) => {
      if (!seenIds.has(s.id)) {
        seenIds.add(s.id);
        suggestions.push(s);
      }
    });
  }

  return suggestions;
}

export default useSyncSuggestionsMap;
